// Shades derived from a reseller's accent colour, for the soft "info" surfaces in the branded
// pages (notice panels, icon tiles). These used to be hardcoded to tints of the default teal,
// which meant a reseller's accent colour left them untouched and clashing.
//
// Only 6-digit hex can be manipulated safely here, so any other notation (a named colour,
// rgb(), an 8-digit hex) falls back to the literal the design shipped with rather than
// producing something invalid.
//
// The alpha variants assume they sit on a white card, which is where the branded pages use
// them - the design's original #E6F2F1 tint is exactly the default accent at 10% over white.

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(color: string): boolean {
    return HEX_COLOR.test(color);
}

/** `accentColor` at `alpha` (0-1) opacity, as an 8-digit hex. */
export function accentAlpha(accentColor: string, alpha: number, fallback: string): string {
    if (!isHexColor(accentColor)) return fallback;
    const clamped = Math.max(0, Math.min(1, alpha));
    const suffix = Math.round(clamped * 255).toString(16).padStart(2, "0");
    return `${accentColor}${suffix}`;
}

/**
 * `accentColor` darkened towards black by `factor` (0-1, where 1 is unchanged). Used for text
 * that has to stay readable on an accentAlpha() surface - lightening via alpha would drop the
 * contrast ratio, so text needs a darker shade of the same hue instead.
 */
export function accentShade(accentColor: string, factor: number, fallback: string): string {
    if (!isHexColor(accentColor)) return fallback;
    const clamped = Math.max(0, Math.min(1, factor));
    const channels = [1, 3, 5].map((offset: number) => {
        const value = parseInt(accentColor.substr(offset, 2), 16);
        return Math.round(value * clamped).toString(16).padStart(2, "0");
    });
    return `#${channels.join("")}`;
}
